import React, { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Plus, Users, User, Calendar, Clock, Euro, Trash2, Edit, CalendarPlus, ChevronDown, ChevronRight, AlertTriangle, Info, Play, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { format, eachDayOfInterval, isWeekend, differenceInDays } from "date-fns";
import { fr } from "date-fns/locale";
import AffectationForm from "./AffectationForm";
import { useAffectationsRealtime } from "@/hooks/useAffectationsRealtime";
import { EquipeIcon } from "@/components/equipe/EquipeIcon";
import { ModalAffectationChantierBulk } from "@/components/equipe/ModalAffectationChantierBulk";
import { useQueryClient } from '@tanstack/react-query';
import { invalidateChantier } from '@/lib/queryInvalidation';
import { cn } from "@/lib/utils";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { JourDetail } from "@/types/calendar";
import { HeuresSupSection } from "@/components/heures-sup";
import { useHeuresSupSettings } from "@/hooks/useHeuresSup";

// Déclaration du type d'événement custom pour la synchronisation temps réel
declare global {
  interface WindowEventMap {
    'chantier-metrics-updated': CustomEvent<{ chantierId: string }>;
  }
}

/**
 * Calcule les jours ouvrés normaux (lundi-vendredi) dans une période
 */
const calculerJoursOuvresNormaux = (debut: Date, fin: Date): number => {
  const jours = eachDayOfInterval({ start: debut, end: fin });
  return jours.filter(jour => !isWeekend(jour)).length;
};

/**
 * Calcule les jours supplémentaires travaillés (weekends/fériés)
 * = jours_travailles - jours ouvrés normaux dans la période
 */
const calculerJoursSupplementaires = (dateDebut: string, dateFin: string, joursTravailles: number): number => {
  const debut = new Date(dateDebut);
  const fin = new Date(dateFin);
  const joursOuvresNormaux = calculerJoursOuvresNormaux(debut, fin);
  // Si on a travaillé plus de jours que les jours ouvrés, ce sont des jours supp (weekends)
  return Math.max(0, joursTravailles - joursOuvresNormaux);
};

/**
 * Formate les jours_details pour affichage
 * Retourne un résumé des demi-journées sélectionnées
 */
const formatJoursDetails = (joursDetails: JourDetail[] | null | undefined): string | null => {
  if (!joursDetails || joursDetails.length === 0) return null;

  // Grouper par date
  const grouped = new Map<string, string[]>();
  joursDetails.forEach(detail => {
    const periods = grouped.get(detail.date) || [];
    if (detail.period === 'FULL') {
      periods.push('M', 'A');
    } else {
      periods.push(detail.period === 'AM' ? 'M' : 'A');
    }
    grouped.set(detail.date, periods);
  });

  // Formater
  const lines: string[] = [];
  grouped.forEach((periods, date) => {
    const dateFormatted = format(new Date(date), 'dd/MM', { locale: fr });
    const isFullDay = periods.includes('M') && periods.includes('A');
    if (isFullDay) {
      lines.push(`${dateFormatted}: Journée`);
    } else {
      lines.push(`${dateFormatted}: ${periods.join(' + ')}`);
    }
  });

  return lines.join('\n');
};

interface AffectationsListProps {
  chantierId: string;
  entrepriseId: string;
  isHighlighted?: boolean;
}

const AffectationsList = ({ chantierId, entrepriseId, isHighlighted = false }: AffectationsListProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [affectations, setAffectations] = useState<any[]>([]);
  const [membres, setMembres] = useState<any[]>([]);
  const [equipes, setEquipes] = useState<any[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  // Arrêter le clignotement dès qu'un bouton d'ajout est cliqué
  const [dismissed, setDismissed] = useState(false);
  const showPulse = isHighlighted && !dismissed;
  const [editingAffectation, setEditingAffectation] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deletingPeriodKey, setDeletingPeriodKey] = useState<string | null>(null);

  // États pour le modal d'affectation bulk (#12)
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [chantierNom, setChantierNom] = useState<string>('');
  const [chantierDateDebut, setChantierDateDebut] = useState<string | null>(null);
  const [chantierDateFin, setChantierDateFin] = useState<string | null>(null);
  // Affectations actives de tous les membres (pour afficher leur statut dans le modal)
  const [affectationsActives, setAffectationsActives] = useState<Map<string, { chantier_nom: string; date_fin: string }>>(new Map());

  // Hook pour les paramètres heures supplémentaires
  const { settings: heuresSupSettings, isLoading: loadingSettings } = useHeuresSupSettings(entrepriseId);

  useEffect(() => {
    loadData();
  }, [chantierId]);

  // Subscription Realtime pour les affectations
  useAffectationsRealtime(chantierId, () => {
    if (import.meta.env.DEV) console.log('[AffectationsList] Changement détecté, rechargement...');
    loadData();
  });

  const loadData = async () => {
    setLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0];

      const [affectationsRes, membresRes, equipesRes, chantierRes, affectationsActivesRes] = await Promise.all([
        supabase
          .from("affectations_chantiers")
          .select("*")
          .eq("chantier_id", chantierId)
          .order("date_debut", { ascending: false }),
        supabase
          .from("membres_equipe")
          .select("*")
          .eq("entreprise_id", entrepriseId),
        supabase
          .from("equipes")
          .select("*")
          .eq("entreprise_id", entrepriseId),
        // Charger le nom du chantier pour le modal bulk
        supabase
          .from("chantiers")
          .select("nom_chantier, date_debut_prevue, date_fin_estimee")
          .eq("id", chantierId)
          .single(),
        // Charger les affectations actives de tous les membres (pour le modal bulk)
        supabase
          .from("affectations_chantiers")
          .select("membre_equipe_id, date_fin, chantiers(nom_chantier)")
          .gte("date_fin", today)
          .lte("date_debut", today),
      ]);

      setAffectations(affectationsRes.data || []);
      setMembres(membresRes.data || []);
      setEquipes(equipesRes.data || []);
      setChantierNom(chantierRes.data?.nom_chantier || '');
      setChantierDateDebut(chantierRes.data?.date_debut_prevue || null);
      setChantierDateFin(chantierRes.data?.date_fin_estimee || null);

      // Construire la map des affectations actives par membre
      const affMap = new Map<string, { chantier_nom: string; date_fin: string }>();
      (affectationsActivesRes.data || []).forEach((aff: any) => {
        // Ne garder que la première affectation trouvée pour chaque membre (la plus récente)
        if (!affMap.has(aff.membre_equipe_id)) {
          affMap.set(aff.membre_equipe_id, {
            chantier_nom: aff.chantiers?.nom_chantier || 'Chantier inconnu',
            date_fin: aff.date_fin,
          });
        }
      });
      setAffectationsActives(affMap);
    } catch (error) {
      console.error("Erreur chargement affectations:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Supprimer cette affectation ?")) return;
    setDeletingId(id);

    try {
      const { error } = await supabase
        .from("affectations_chantiers")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast({ title: "Affectation supprimée" });

      // Émettre l'événement pour synchroniser les composants non migrés (page Équipe, sidebar, etc.)
      window.dispatchEvent(new CustomEvent('chantier-metrics-updated', {
        detail: { chantierId }
      }));
      // Invalider le cache React Query (métriques, finances, charts)
      invalidateChantier(queryClient, entrepriseId, chantierId);

      loadData();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setDeletingId(null);
    }
  };

  // Supprimer toutes les affectations d'une période
  // Si c'est une période de retard, supprime aussi le frais correspondant et recalcule les métriques
  const handleDeletePeriod = async (affectationIds: string[], periodLabel: string, isRetard: boolean = false) => {
    const message = isRetard
      ? `Supprimer le retard (${affectationIds.length} affectation(s)) et les coûts associés ?`
      : `Supprimer les ${affectationIds.length} affectation(s) de cette période (${periodLabel}) ?`;

    if (!confirm(message)) return;
    setDeletingPeriodKey(periodLabel);

    try {
      // 1. Supprimer les affectations
      const { error: affError } = await supabase
        .from("affectations_chantiers")
        .delete()
        .in("id", affectationIds);

      if (affError) throw affError;

      // 2. Si c'est un retard, supprimer aussi les frais associés
      if (isRetard) {
        const { error: fraisError } = await supabase
          .from("frais_chantier")
          .delete()
          .eq("chantier_id", chantierId)
          .or('type_frais.ilike.%Pénalité de retard%,type_frais.ilike.%Coût retard%');

        if (fraisError) {
          console.error("Erreur suppression frais retard:", fraisError);
          // On continue quand même, l'important c'est que les affectations soient supprimées
        }
      }

      // 3. Recalculer les métriques du chantier pour mettre à jour la rentabilité (POUR TOUTES LES SUPPRESSIONS)
      if (import.meta.env.DEV) console.log('[AffectationsList] Recalcul des métriques après suppression...');
      const { data: metricsData, error: metricsError } = await supabase
        .rpc('compute_chantier_metrics', { p_chantier: chantierId });

      if (metricsError) {
        console.error("Erreur recalcul métriques:", metricsError);
      } else if (metricsData) {
        // 4. Stocker les métriques recalculées pour le realtime
        const { error: upsertError } = await supabase
          .from('chantier_metrics_realtime')
          .upsert({
            chantier_id: chantierId,
            metrics: metricsData,
            updated_at: new Date().toISOString()
          });

        if (upsertError) {
          console.error("Erreur stockage métriques:", upsertError);
        }
      }

      // 5. Toast de confirmation
      if (isRetard) {
        toast({ title: "Retard supprimé", description: "Les coûts de retard ont été effacés et la rentabilité recalculée." });
      } else {
        toast({ title: `${affectationIds.length} affectation(s) supprimée(s)` });
      }

      // 6. Émettre un événement global pour notifier les composants non migrés (sidebar, planning)
      if (import.meta.env.DEV) console.log('[AffectationsList] Émission événement chantier-metrics-updated pour:', chantierId);
      window.dispatchEvent(new CustomEvent('chantier-metrics-updated', {
        detail: { chantierId }
      }));
      // Invalider le cache React Query (métriques, finances, charts)
      invalidateChantier(queryClient, entrepriseId, chantierId);

      loadData();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setDeletingPeriodKey(null);
    }
  };

  const handleEdit = (affectation: any) => {
    setEditingAffectation(affectation);
    setDialogOpen(true);
  };

  const handleSuccess = () => {
    setDialogOpen(false);
    setEditingAffectation(null);
    loadData();
  };

  // État pour gérer les sections collapsées
  const [collapsedPeriods, setCollapsedPeriods] = useState<Set<string>>(new Set());
  const [collapsedEquipes, setCollapsedEquipes] = useState<Set<string>>(new Set());

  const togglePeriod = (periodKey: string) => {
    setCollapsedPeriods(prev => {
      const next = new Set(prev);
      if (next.has(periodKey)) next.delete(periodKey);
      else next.add(periodKey);
      return next;
    });
  };

  const toggleEquipe = (equipeKey: string) => {
    setCollapsedEquipes(prev => {
      const next = new Set(prev);
      if (next.has(equipeKey)) next.delete(equipeKey);
      else next.add(equipeKey);
      return next;
    });
  };

  /**
   * Groupement hybride : par période (date_debut → date_fin) puis par équipe
   * Structure : { periodKey: { equipeKey: affectations[] } }
   */
  const groupedByPeriodAndEquipe = useMemo(() => {
    // Étape 1: Grouper par période unique (date_debut + date_fin + isRetard)
    const periodMap = new Map<string, {
      dateDebut: string;
      dateFin: string;
      isRetard: boolean;
      isReprise: boolean;
      label: string;
      equipes: Map<string, any[]>;
    }>();

    affectations.forEach(aff => {
      const isRetard = aff.notes?.startsWith('Retard chantier');
      const isReprise = aff.notes?.startsWith('Reprise chantier');
      const periodType = isRetard ? 'retard' : isReprise ? 'reprise' : 'normal';
      const periodKey = `${aff.date_debut}_${aff.date_fin}_${periodType}`;

      if (!periodMap.has(periodKey)) {
        periodMap.set(periodKey, {
          dateDebut: aff.date_debut,
          dateFin: aff.date_fin,
          isRetard,
          isReprise,
          label: isRetard ? 'Retard' : isReprise ? 'Reprise de chantier' : 'Période initiale',
          equipes: new Map()
        });
      }

      const period = periodMap.get(periodKey)!;

      // Trouver l'équipe du membre
      const membre = membres.find(m => m.id === aff.membre_equipe_id);
      const equipeId = membre?.equipe_id || 'sans-equipe';
      const equipe = equipes.find(e => e.id === equipeId);
      const equipeKey = equipeId;

      if (!period.equipes.has(equipeKey)) {
        period.equipes.set(equipeKey, []);
      }
      period.equipes.get(equipeKey)!.push({
        ...aff,
        membre,
        equipe: equipe || null
      });
    });

    // Trier les périodes : d'abord les périodes normales, puis les retards
    const sortedPeriods = Array.from(periodMap.entries()).sort((a, b) => {
      // Les périodes normales d'abord
      if (a[1].isRetard !== b[1].isRetard) {
        return a[1].isRetard ? 1 : -1;
      }
      // Puis par date de début
      return new Date(a[1].dateDebut).getTime() - new Date(b[1].dateDebut).getTime();
    });

    return sortedPeriods;
  }, [affectations, membres, equipes]);

  type AffectationGroup = [string, any[]];

  /**
   * Calcule le coût d'une affectation en prenant en compte heures_dernier_jour
   * Si heures_dernier_jour est défini, on calcule :
   * - (jours_travailles - 1) jours complets × heures_par_jour
   * - + 1 dernier jour × heures_dernier_jour
   */
  const calculerCoutAffectation = (aff: any, tauxHoraire: number, coefficientCharges: number) => {
    const heuresParJour = aff.heures_par_jour || 8;
    const multiplicateur = coefficientCharges;

    // Si heures_dernier_jour est défini (retrait anticipé)
    if (aff.heures_dernier_jour !== null && aff.heures_dernier_jour !== undefined) {
      const joursComplets = Math.max(0, aff.jours_travailles - 1);
      const heuresDernierJour = aff.heures_dernier_jour;

      const coutJoursComplets = joursComplets * heuresParJour * tauxHoraire * multiplicateur;
      const coutDernierJour = heuresDernierJour * tauxHoraire * multiplicateur;

      return coutJoursComplets + coutDernierJour;
    }

    // Sinon calcul standard
    return aff.jours_travailles * heuresParJour * tauxHoraire * multiplicateur;
  };

  // Calculer les totaux avec taux réels des membres et coefficient_charges
  const totalCout = affectations.reduce((sum, aff) => {
    const membre = membres.find(m => m.id === aff.membre_equipe_id);
    if (!membre) return sum;

    const tauxHoraire = aff.taux_horaire_specifique ?? membre.taux_horaire;
    const coef = (membre as any).coefficient_charges || 1.85;

    return sum + calculerCoutAffectation(aff, tauxHoraire, coef);
  }, 0);

  const totalJours = affectations.reduce((sum, aff) => sum + aff.jours_travailles, 0);

  // Membres affectés uniques pour les heures supplémentaires
  const membresAffectes = useMemo(() => {
    const uniqueMembreIds = [...new Set(affectations.map(aff => aff.membre_equipe_id))];
    return uniqueMembreIds
      .map(membreId => membres.find(m => m.id === membreId))
      .filter(Boolean)
      .map(m => ({
        id: m.id,
        nom: m.nom,
        prenom: m.prenom,
        taux_horaire: m.taux_horaire || 0,
      }));
  }, [affectations, membres]);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Users className="h-4 w-4" />
              Équipe affectée
            </CardTitle>
            <CardDescription className="text-xs">
              Gérez les périodes de travail des membres et équipes
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => { setDismissed(true); setBulkModalOpen(true); }}
              disabled={membres.length === 0}
            >
              <Users className="h-3.5 w-3.5 mr-1.5" />
              Sélection multiple
            </Button>
            <Button
              size="sm"
              onClick={() => { setDismissed(true); setEditingAffectation(null); setDialogOpen(true); }}
              className={cn(
                "bg-yellow-500 hover:bg-yellow-600 text-black border-yellow-500 font-semibold",
                showPulse && "animate-highlight-pulse"
              )}
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              Un membre / une équipe
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        {loading ? (
          <div className="text-center py-8 text-muted-foreground">Chargement...</div>
        ) : groupedByPeriodAndEquipe.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Aucune affectation</p>
            <p className="text-sm">Affectez des membres ou équipes pour commencer</p>
          </div>
        ) : (
          <>
            {/* Groupement par période puis par équipe */}
            {groupedByPeriodAndEquipe.map(([periodKey, periodData]) => {
              const isPeriodCollapsed = collapsedPeriods.has(periodKey);

              // Calculs pour cette période
              const allAffsPeriod = Array.from(periodData.equipes.values()).flat();
              // Jours calendaires réels de la période (inclusif)
              const joursCalendaires = differenceInDays(
                new Date(periodData.dateFin),
                new Date(periodData.dateDebut)
              ) + 1;
              // Total jours-homme (somme des jours travaillés de tous les membres)
              const totalJoursHomme = allAffsPeriod.reduce((sum, a) => sum + a.jours_travailles, 0);
              const totalCoutPeriod = allAffsPeriod.reduce((sum, aff) => {
                const membre = aff.membre;
                if (!membre) return sum;
                const tauxHoraire = aff.taux_horaire_specifique ?? membre.taux_horaire;
                const coef = (membre as any).coefficient_charges || 1.85;
                return sum + calculerCoutAffectation(aff, tauxHoraire, coef);
              }, 0);

              return (
                <Collapsible
                  key={periodKey}
                  open={!isPeriodCollapsed}
                  onOpenChange={() => togglePeriod(periodKey)}
                  className="space-y-2"
                >
                  {/* En-tête de la période */}
                  <CollapsibleTrigger asChild>
                    <div className={cn(
                      "flex items-center justify-between p-3 rounded-lg cursor-pointer hover:bg-muted/50 transition-colors",
                      periodData.isRetard
                        ? "bg-orange-500/10 border border-orange-500/30"
                        : periodData.isReprise
                        ? "bg-green-500/10 border border-green-500/30"
                        : "bg-blue-500/5 border border-blue-500/20"
                    )}>
                      <div className="flex items-center gap-3">
                        {isPeriodCollapsed ? (
                          <ChevronRight className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}

                        {periodData.isRetard ? (
                          <AlertTriangle className="h-4 w-4 text-orange-500" />
                        ) : periodData.isReprise ? (
                          <Play className="h-4 w-4 text-green-500" />
                        ) : (
                          <Calendar className="h-4 w-4 text-blue-500" />
                        )}

                        <div>
                          <p className="font-semibold text-sm flex items-center gap-2">
                            {format(new Date(periodData.dateDebut), "dd/MM", { locale: fr })} → {format(new Date(periodData.dateFin), "dd/MM", { locale: fr })}
                            <Badge
                              variant={periodData.isRetard ? "destructive" : periodData.isReprise ? "default" : "secondary"}
                              className={cn(
                                "text-[10px] py-0",
                                periodData.isRetard && "bg-orange-500",
                                periodData.isReprise && "bg-green-500"
                              )}
                            >
                              {periodData.label}
                            </Badge>
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {periodData.equipes.size} équipe(s) • {allAffsPeriod.length} membre(s)
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <div className="text-right">
                          <p className="font-bold text-sm">
                            {totalCoutPeriod.toFixed(2)} €
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {joursCalendaires} jour{joursCalendaires > 1 ? 's' : ''}
                          </p>
                        </div>
                        {(() => {
                          const periodLabel = `${format(new Date(periodData.dateDebut), "dd/MM", { locale: fr })} → ${format(new Date(periodData.dateFin), "dd/MM", { locale: fr })}`;
                          const isDeletingPeriod = deletingPeriodKey === periodLabel;
                          return (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 px-2 text-muted-foreground hover:text-destructive hover:border-destructive hover:bg-destructive/10"
                              disabled={isDeletingPeriod}
                              onClick={(e) => {
                                e.stopPropagation();
                                const allIds = allAffsPeriod.map(a => a.id);
                                handleDeletePeriod(allIds, periodLabel, periodData.isRetard);
                              }}
                              title={periodData.isRetard ? "Supprimer le retard et les coûts associés" : "Supprimer toutes les affectations de cette période"}
                            >
                              {isDeletingPeriod ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                            </Button>
                          );
                        })()}
                      </div>
                    </div>
                  </CollapsibleTrigger>

                  <CollapsibleContent className="pl-4 space-y-2">
                    {/* Équipes dans cette période */}
                    {Array.from(periodData.equipes.entries()).map(([equipeKey, affsEquipe]) => {
                      const equipe = affsEquipe[0]?.equipe;
                      const equipeDisplayKey = `${periodKey}_${equipeKey}`;
                      const isEquipeCollapsed = collapsedEquipes.has(equipeDisplayKey);

                      // Calculs pour cette équipe
                      const totalJoursEquipe = affsEquipe.reduce((sum, a) => sum + a.jours_travailles, 0);
                      const totalCoutEquipe = affsEquipe.reduce((sum, aff) => {
                        const membre = aff.membre;
                        if (!membre) return sum;
                        const tauxHoraire = aff.taux_horaire_specifique ?? membre.taux_horaire;
                        const coef = (membre as any).coefficient_charges || 1.85;
                        return sum + calculerCoutAffectation(aff, tauxHoraire, coef);
                      }, 0);

                      return (
                        <Collapsible
                          key={equipeDisplayKey}
                          open={!isEquipeCollapsed}
                          onOpenChange={() => toggleEquipe(equipeDisplayKey)}
                        >
                          <div className="border rounded-lg overflow-hidden">
                            {/* En-tête de l'équipe */}
                            <CollapsibleTrigger asChild>
                              <div className="flex items-center justify-between p-2.5 bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors">
                                <div className="flex items-center gap-2">
                                  {isEquipeCollapsed ? (
                                    <ChevronRight className="h-3.5 w-3.5" />
                                  ) : (
                                    <ChevronDown className="h-3.5 w-3.5" />
                                  )}

                                  {equipe?.pictogramme && equipe?.couleur ? (
                                    <EquipeIcon
                                      pictogramme={equipe.pictogramme}
                                      couleur={equipe.couleur}
                                      size="sm"
                                      showBackground={true}
                                    />
                                  ) : (
                                    <Users className="h-4 w-4 text-muted-foreground" />
                                  )}

                                  <div>
                                    <p className="font-medium text-sm">
                                      {equipe?.nom || 'Sans équipe'}
                                      <span className="text-muted-foreground ml-1">({affsEquipe.length})</span>
                                    </p>
                                  </div>
                                </div>

                                <div className="text-right text-sm">
                                  <span className="font-mono font-semibold">{totalCoutEquipe.toFixed(2)} €</span>
                                  <span className="text-muted-foreground ml-2">| {totalJoursEquipe} j-h</span>
                                </div>
                              </div>
                            </CollapsibleTrigger>

                            <CollapsibleContent>
                              <div className="p-2 space-y-1.5">
                                {/* Membres de l'équipe */}
                                {affsEquipe.map((aff) => {
                                  const membre = aff.membre;
                                  if (!membre) return null;

                                  const tauxHoraire = aff.taux_horaire_specifique ?? membre.taux_horaire;
                                  const coef = (membre as any).coefficient_charges || 1.85;
                                  const coutAff = calculerCoutAffectation(aff, tauxHoraire, coef);
                                  const joursSupp = calculerJoursSupplementaires(aff.date_debut, aff.date_fin, aff.jours_travailles);

                                  // Détecter si c'est une affectation différée (créée par Option D)
                                  const isDifferee = aff.notes?.toLowerCase().includes('différée');

                                  return (
                                    <div key={aff.id} className={cn(
                                      "flex items-center justify-between py-1.5 px-2 bg-background rounded text-xs border",
                                      isDifferee && "border-amber-500/50 bg-amber-500/5"
                                    )}>
                                      <div className="flex items-center gap-3">
                                        <User className={cn(
                                          "h-3.5 w-3.5",
                                          isDifferee ? "text-amber-500" : "text-muted-foreground"
                                        )} />
                                        <span className="font-medium">{membre.prenom} {membre.nom}</span>
                                        {isDifferee && (
                                          <Badge variant="outline" className="text-[10px] py-0 border-amber-500 text-amber-500 bg-amber-500/10">
                                            <Clock className="h-2.5 w-2.5 mr-0.5" />
                                            Différée
                                          </Badge>
                                        )}
                                        {/* Badge jours avec détails si jours_details existe */}
                                        {aff.jours_details && aff.jours_details.length > 0 ? (
                                          <HoverCard>
                                            <HoverCardTrigger asChild>
                                              <Badge variant="secondary" className="text-xs py-0 cursor-help flex items-center gap-1">
                                                {aff.jours_travailles}j
                                                <Info className="h-2.5 w-2.5" />
                                              </Badge>
                                            </HoverCardTrigger>
                                            <HoverCardContent className="w-auto p-3" side="top">
                                              <div className="text-xs space-y-1">
                                                <p className="font-semibold mb-2">Détail des jours travaillés :</p>
                                                {formatJoursDetails(aff.jours_details)?.split('\n')?.map((line, i) => (
                                                  <p key={i} className="text-muted-foreground">{line}</p>
                                                ))}
                                              </div>
                                            </HoverCardContent>
                                          </HoverCard>
                                        ) : (
                                          <Badge variant="secondary" className="text-xs py-0">
                                            {aff.heures_dernier_jour !== null && aff.heures_dernier_jour !== undefined
                                              ? `${Math.max(0, aff.jours_travailles - 1)}j + ${aff.heures_dernier_jour}h`
                                              : `${aff.jours_travailles}j`
                                            }
                                          </Badge>
                                        )}
{/* Badge jours supp masqué - le calcul reste actif pour la rentabilité */}
                                        <span className="text-muted-foreground flex items-center gap-1">
                                          <Clock className="h-3 w-3" />
                                          {aff.heures_par_jour}h/j
                                        </span>
                                        <span className="font-mono text-muted-foreground">
                                          {coutAff.toFixed(2)} €
                                        </span>
                                      </div>
                                      <div className="flex gap-0.5">
                                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleEdit(aff)}>
                                          <Edit className="h-3 w-3" />
                                        </Button>
                                        <Button variant="ghost" size="icon" className="h-6 w-6" disabled={deletingId === aff.id} onClick={() => handleDelete(aff.id)}>
                                          {deletingId === aff.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                                        </Button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </CollapsibleContent>
                          </div>
                        </Collapsible>
                      );
                    })}
                  </CollapsibleContent>
                </Collapsible>
              );
            })}

            <div className="border-t pt-3 flex items-center justify-between text-sm font-bold">
              <span>💰 COÛT TOTAL MAIN D'ŒUVRE:</span>
              <span className="text-primary">{totalCout.toFixed(2)} €</span>
            </div>
            <p className="text-xs text-muted-foreground text-right">
              {totalJours} jours-homme travaillés
            </p>
          </>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingAffectation?.id ? "Modifier l'affectation" : "Affecter un membre/équipe"}
              </DialogTitle>
              <DialogDescription>
                Définissez la période et les conditions de travail
              </DialogDescription>
            </DialogHeader>
            <AffectationForm
              chantierId={chantierId}
              entrepriseId={entrepriseId}
              affectation={editingAffectation}
              onSuccess={handleSuccess}
              onCancel={() => { setDialogOpen(false); setEditingAffectation(null); }}
            />
          </DialogContent>
        </Dialog>

        {/* Modal d'affectation bulk (#12) - affecte plusieurs membres en une fois */}
        <ModalAffectationChantierBulk
          isOpen={bulkModalOpen}
          onClose={() => setBulkModalOpen(false)}
          membres={membres.map(m => ({
            id: m.id,
            prenom: m.prenom,
            nom: m.nom,
            poste: m.poste,
            taux_horaire: m.taux_horaire,
            equipe_id: m.equipe_id,
            statut: m.statut,
            affectation_actuelle: affectationsActives.get(m.id),
          }))}
          equipes={equipes.map(eq => ({
            id: eq.id,
            nom: eq.nom,
            couleur: eq.couleur,
            pictogramme: eq.pictogramme,
          }))}
          entrepriseId={entrepriseId}
          onSuccess={() => {
            loadData();
            // Émettre l'événement pour synchroniser les composants non migrés
            window.dispatchEvent(new CustomEvent('chantier-metrics-updated', {
              detail: { chantierId }
            }));
            // Invalider le cache React Query
            invalidateChantier(queryClient, entrepriseId, chantierId);
          }}
          preSelectedChantierId={chantierId}
          preSelectedChantierNom={chantierNom}
          preSelectedDateDebut={chantierDateDebut || undefined}
          preSelectedDateFin={chantierDateFin || undefined}
        />
      </CardContent>

      {/* Section Heures Supplémentaires - visible uniquement si activé */}
      {heuresSupSettings.heures_sup_actives && membresAffectes.length > 0 && (
        <HeuresSupSection
          chantierId={chantierId}
          entrepriseId={entrepriseId}
          membresAffectes={membresAffectes}
          settings={heuresSupSettings}
        />
      )}
    </Card>
  );
};

export default AffectationsList;
